import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import history from 'common/history';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faExternalLinkAlt,
  faLocationArrow,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import {
  selectBundleTabPage,
  selectCurrentCase,
  selectCurrentFolder,
  selectDocSearchLocation,
  selectDocSearchVal,
  selectDocSearchLocationForCompare,
  selectDocSearchValForCompare,
  selectDocuments,
  selectFileToCompare,
  selectGlobalSideBarOpen,
  selectGlobalSummaryOpen,
  selectIsGlobalPageLookup,
  selectMatchedUrl,
  selectPresentQuery,
  selectSpecificParam,
  selectUseAskAiSSEChat,
  selectUserAiTools,
  selectAskAiSearchType,
  selectAskAiSseHasResponseArea,
} from 'common/selectors';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import { openNewBackgroundTab } from 'utils/browser';
import { fileTypes } from '../enums';
import equal from 'react-fast-compare';
import DocumentSearch from '../DocumentSearch';
import FileViewer from '../FileViewer';
import useFolderSpecificFileDetails from './hooks/useFolderSpecificFileDetails';
import ConfidentialityRing from './ConfidentialityRing';
import { usePrevious } from 'utils/hooks';
import { useDispatch } from 'react-redux';
import { setCurrentSelectedFile } from '../redux/setCurrentSelectedFile';
import { setLastSelectedFile } from '../redux/setLastSelectedFile';
import { setFileToCompare } from '../redux/setFileToCompare';
import { useFilteredAndSortedData } from '../redux/setFilteredAndSortedData';
import { IconButton, Tooltip, Menu, MenuItem } from '@mui/material';
import T from 'i18n';
import {
  Close,
  PhotoFilter,
  AutoAwesome,
  Search,
  FilePresent,
  AttachEmail,
  CenterFocusStrong,
  FilterCenterFocus,
} from '@mui/icons-material';
import { setSidebarOpen, setSummaryOpen } from 'features/common/redux/navbarLayout';
import GlobalPagingBreadcrumbItem from './GlobalPagingBreadCrumbItem';
import { useSetAISearchState } from '../redux/setAISearchState';
import { fetchFileDetails } from '../redux/actions';
import { useFocusViewBridge } from '../FocusViewBridge';

const ATTACHMENT_NAME_MAX_LENGTH = 50;

type ViewerWrapperProps = {
  fileId: string;
  compareFileName?: string;
  fileType: string;
  isDocFinder: boolean;
  fileData?: any;
  isFileToCompare?: boolean;
  canShowSummaryPane?: boolean;
  onSearchClick?: () => void;
  onOpenViewDrawer?: (row: any) => void;
};

export default ({
  fileId,
  compareFileName,
  fileType,
  fileData,
  isFileToCompare,
  isDocFinder,
  canShowSummaryPane,
  onSearchClick,
  onOpenViewDrawer,
}: ViewerWrapperProps) => {
  const dispatch = useDispatch();
  const { searchMatchesInput } = useSetAISearchState();
  const focusViewBridge = useFocusViewBridge();

  // Extract compare files from search matches input
  const compareFileData = useMemo(() => {
    return searchMatchesInput?.[0]?.compare?.files ?? [];
  }, [searchMatchesInput]);

  const isBundleTabPage = useSelector(selectBundleTabPage);
  const caseId = useSelector(selectCurrentCase).id;
  const currentFolder = useSelector(selectCurrentFolder);
  const currentUrl = useSelector(selectMatchedUrl);
  const isTrialBundle = currentFolder?.type === 'trialbook';
  const isGlobalPageLookUp = useSelector(selectIsGlobalPageLookup) as boolean;
  const docSearchVal = useSelector(selectDocSearchVal);
  const docSearchLocation = useSelector(selectDocSearchLocation);
  const docSearchValForCompare = useSelector(selectDocSearchValForCompare);
  const docSearchLocationForCompare = useSelector(selectDocSearchLocationForCompare);
  const askAiSearchType = useSelector(selectAskAiSearchType);
  const fileToCompareMetaData = useSelector(selectFileToCompare);
  const present = useSelector(selectPresentQuery);
  const documents = useSelector(selectDocuments) as Array<any>;
  const useAskAiSSEChat = useSelector(selectUseAskAiSSEChat);
  const userAiTools = useSelector(selectUserAiTools);
  const askAiSseHasResponseArea = useSelector(selectAskAiSseHasResponseArea);
  const file = useSelector((state) => selectSpecificParam(state, 'file'));
  const prevFile = usePrevious(file);
  const openGlobally = useSelector(selectGlobalSideBarOpen);
  const summaryOpen = useSelector(selectGlobalSummaryOpen);
  const { filteredAndSortedData } = useFilteredAndSortedData();
  const requiredDocumentsData = filteredAndSortedData || documents;
  const globalPaging = currentFolder?.globalPaging;
  const isPresentModePage = currentUrl.includes(`${caseId}/present-`);
  const isTagsFolder = currentFolder?.type === 'tags';
  const urlParams = new URLSearchParams(window.location.search);
  const currentPathname = history.location?.pathname || window.location.pathname;
  const hasAskAIParam = urlParams.has('askAI');
  const hasFileAskAIParam = urlParams.has('fileAskAI');
  const isAskAI = urlParams.get('askAI');
  const isFileAskAI = urlParams.get('fileAskAI');
  const fileViewerRef = useRef<any>();
  const [, setSearchDetails] = useState(null); // searchDetails value is not used
  const [searchFunctions, setSearchFunctions] = useState(null);
  const [attachmentsMenuAnchorEl, setAttachmentsMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [attachedToMenuAnchorEl, setAttachedToMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [attachmentIndicatorHintsByFileId, setAttachmentIndicatorHintsByFileId] = useState<
    Record<string, { attachments: any[]; isAttachedTo: any[] }>
  >({});
  const isSearchMode = currentUrl.includes(`${caseId}/search-mode`);
  const isSearchModeRefreshed = file && documents.length === 0 && isSearchMode;
  const showSearchForFile = askAiSearchType === 'multiFiles' || askAiSearchType === 'bundle';
  const { currentFileMetaData, bundleSpecificFileDetails } = useFolderSpecificFileDetails(
    isTrialBundle,
    globalPaging,
    isGlobalPageLookUp,
    isPresentModePage,
    isTagsFolder,
    isSearchModeRefreshed,
    fileData,
    isFileToCompare,
  );

  const focusViewRow = useMemo(() => {
    if (currentFileMetaData) return currentFileMetaData;
    if (fileData) return fileData;
    if (fileId) return { id: fileId, fileId };
    return null;
  }, [currentFileMetaData, fileData, fileId]);
  const handleOpenFocusView = useCallback(() => {
    if (!focusViewRow) return;
    if (onOpenViewDrawer) {
      onOpenViewDrawer(focusViewRow);
      return;
    }
    focusViewBridge?.openFocusView(focusViewRow);
  }, [focusViewBridge, focusViewRow, onOpenViewDrawer]);
  const canOpenFocusView = Boolean(focusViewRow && (onOpenViewDrawer || focusViewBridge));
  const getResolvedFileIdFromMeta = useCallback((meta: any) => {
    if (!meta) return null;
    return meta.fileId || meta.id || meta.folderFileId || meta.file?.fileId || meta.file?.id || null;
  }, []);

  const getAttachmentRelationships = useCallback((meta: any) => {
    if (!meta) {
      return { attachments: null, attachedToDocuments: null };
    }
    const attachmentsCandidates = [
      meta.attachments,
      meta.file?.attachments,
      meta.fileData?.attachments,
      meta.document?.attachments,
    ];
    const attachedToCandidates = [
      meta.isAttachedTo,
      meta.file?.isAttachedTo,
      meta.fileData?.isAttachedTo,
      meta.document?.isAttachedTo,
    ];
    const attachments = attachmentsCandidates.find((candidate) => Array.isArray(candidate)) || null;
    const attachedToDocuments =
      attachedToCandidates.find((candidate) => Array.isArray(candidate)) || null;
    return { attachments, attachedToDocuments };
  }, []);

  const resolvedMetaFileId = getResolvedFileIdFromMeta(currentFileMetaData);
  const sourceFileId = resolvedMetaFileId || fileId || null;
  const sourceHintKeys = Array.from(
    new Set(
      [sourceFileId, fileId]
        .filter((id) => id !== null && id !== undefined && id !== '')
        .map((id) => String(id)),
    ),
  );
  const currentAttachmentHint = sourceHintKeys.reduce((hint, key) => {
    if (hint) return hint;
    return attachmentIndicatorHintsByFileId[key] || null;
  }, null as { attachments: any[]; isAttachedTo: any[] } | null);
  // When the URL fileId has changed but currentFileMetaData still reflects the previous file,
  // the metadata's attachment relationships are stale — skip them to avoid flickering the old icon.
  const isMetadataStale =
    resolvedMetaFileId && fileId && String(resolvedMetaFileId) !== String(fileId);
  const { attachments: currentAttachmentsSource, attachedToDocuments: currentAttachedToSource } =
    isMetadataStale
      ? { attachments: null, attachedToDocuments: null }
      : getAttachmentRelationships(currentFileMetaData);
  const hintedAttachmentsSource = currentAttachmentHint?.attachments;
  const hintedAttachedToSource = currentAttachmentHint?.isAttachedTo;
  const hintedAttachments = Array.isArray(hintedAttachmentsSource) ? hintedAttachmentsSource : [];
  const hintedAttachedTo = Array.isArray(hintedAttachedToSource) ? hintedAttachedToSource : [];
  const attachments = Array.isArray(currentAttachmentsSource)
    ? currentAttachmentsSource.length > 0
      ? currentAttachmentsSource
      : hintedAttachments
    : hintedAttachments;
  const hasAttachments = attachments.length > 0;
  const attachmentsMenuOpen = Boolean(attachmentsMenuAnchorEl);
  const attachedToDocuments = Array.isArray(currentAttachedToSource)
    ? currentAttachedToSource.length > 0
      ? currentAttachedToSource
      : hintedAttachedTo
    : hintedAttachedTo;
  const hasAttachedTo = attachedToDocuments.length > 0;
  const attachedToMenuOpen = Boolean(attachedToMenuAnchorEl);
  const currentAllDocuments = documents || [];
  const attachmentsHeadingLabel = T.translate('case.fileAttachments');
  const attachedToHeadingLabel = T.translate('generic.parentEmail');
  const attachmentsIdLabel = T.translate('generic.id');
  const attachmentsNameLabel = T.translate('generic.name');
  const currentFileTitle =
    resolvedMetaFileId && String(resolvedMetaFileId) === String(fileId)
      ? currentFileMetaData?.name ||
        currentFileMetaData?.fileName ||
        currentFileMetaData?.file?.name ||
        fileId
      : fileId;
  const navigateToFile = useCallback(
    (targetFileId: string) => {
      if (!targetFileId) return;
      const basePath = history.location.pathname.replace(/\/files.*/, '');
      const presentParam = present ? `?present=${present}` : '';
      history.push(`${basePath}/files/${targetFileId}${presentParam}`);
    },
    [history, present],
  );
  const seedAttachmentIndicatorHint = useCallback(
    (targetDocument: any, source: 'attachments' | 'isAttachedTo') => {
      const sourceDocId = getResolvedFileIdFromMeta(currentFileMetaData);
      if (!sourceDocId) return;
      const sourceDocName =
        currentFileMetaData?.name ||
        currentFileMetaData?.fileName ||
        currentFileMetaData?.file?.name ||
        '';
      const sourceFolderFileId =
        currentFileMetaData?.folderFileId || currentFileMetaData?.file?.folderFileId;
      const targetIds = Array.from(
        new Set(
          [
            getResolvedFileIdFromMeta(targetDocument),
            targetDocument?.id,
            targetDocument?.fileId,
            targetDocument?.folderFileId,
            targetDocument?.file?.id,
            targetDocument?.file?.fileId,
            targetDocument?.file?.folderFileId,
          ]
            .filter((id) => id !== null && id !== undefined && id !== '')
            .map((id) => String(id)),
        ),
      );
      if (!targetIds.length) return;
      setAttachmentIndicatorHintsByFileId((prev) =>
        targetIds.reduce((nextHints, targetId) => {
          nextHints[targetId] =
            source === 'attachments'
              ? {
                  attachments: [],
                  isAttachedTo: [
                    { id: sourceDocId, name: sourceDocName, folderFileId: sourceFolderFileId },
                  ],
                }
              : {
                  attachments: [
                    { id: sourceDocId, name: sourceDocName, folderFileId: sourceFolderFileId },
                  ],
                  isAttachedTo: [],
                };
          return nextHints;
        }, { ...prev }),
      );
    },
    [currentFileMetaData, getResolvedFileIdFromMeta],
  );

  const hydrateAttachmentIndicatorHint = useCallback(
    (targetDocument: any) => {
      const targetFileId = getResolvedFileIdFromMeta(targetDocument);
      if (!targetFileId) return;
      (dispatch as any)(
        fetchFileDetails({
          fileId: targetFileId,
          folderFileId: targetDocument?.folderFileId || targetDocument?.file?.folderFileId,
          firstTrialBookOnly: false,
        }),
      )
        .then((res: any) => {
          if (!res || res.error) return;
          const { attachments: fetchedAttachments, attachedToDocuments: fetchedAttachedTo } =
            getAttachmentRelationships(res);
          const normalizedAttachments = Array.isArray(fetchedAttachments) ? fetchedAttachments : [];
          const normalizedAttachedTo = Array.isArray(fetchedAttachedTo) ? fetchedAttachedTo : [];
          const hintIds = Array.from(
            new Set(
              [
                targetFileId,
                res?.id,
                res?.fileId,
                res?.folderFileId,
                targetDocument?.id,
                targetDocument?.fileId,
                targetDocument?.folderFileId,
                targetDocument?.file?.id,
                targetDocument?.file?.fileId,
                targetDocument?.file?.folderFileId,
              ]
                .filter((id) => id !== null && id !== undefined && id !== '')
                .map((id) => String(id)),
            ),
          );
          if (!hintIds.length) return;
          setAttachmentIndicatorHintsByFileId((prev) =>
            hintIds.reduce((nextHints, hintId) => {
              const existing = prev[hintId];
              nextHints[hintId] = {
                attachments: normalizedAttachments.length > 0
                  ? normalizedAttachments
                  : existing?.attachments || [],
                isAttachedTo: normalizedAttachedTo.length > 0
                  ? normalizedAttachedTo
                  : existing?.isAttachedTo || [],
              };
              return nextHints;
            }, { ...prev }),
          );
        })
        .catch(() => {});
    },
    [dispatch, fetchFileDetails, getAttachmentRelationships, getResolvedFileIdFromMeta],
  );

  const searchHandler = useCallback((sd: any) => {
    setSearchDetails((prevSearchDetails: any) => {
      if (!equal(prevSearchDetails, sd)) return sd;
      return prevSearchDetails;
    });
  }, []); // useCallback to prevent rerendering for file viewer

  const searchFunctionsHandler = useCallback((sf: any) => {
    setSearchFunctions(sf);
  }, []); // useCallback to prevent rerendering for file viewer

  const handleAttachmentsMenuClose = useCallback(() => {
    setAttachmentsMenuAnchorEl(null);
  }, []);

  const handleAttachedToMenuClose = useCallback(() => {
    setAttachedToMenuAnchorEl(null);
  }, []);

  const handleAttachmentsIconClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!hasAttachments) return;
      if (attachmentsMenuAnchorEl) {
        handleAttachmentsMenuClose();
      } else {
        setAttachmentsMenuAnchorEl(event.currentTarget);
      }
    },
    [attachmentsMenuAnchorEl, handleAttachmentsMenuClose, hasAttachments],
  );

  const handleAttachedToIconClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!hasAttachedTo) return;
      if (attachedToMenuAnchorEl) {
        handleAttachedToMenuClose();
      } else {
        setAttachedToMenuAnchorEl(event.currentTarget);
      }
    },
    [attachedToMenuAnchorEl, handleAttachedToMenuClose, hasAttachedTo],
  );

  const handleAttachmentSelection = useCallback(
    (attachment: any, closeMenu: () => void, source: 'attachments' | 'isAttachedTo') => {
      closeMenu();
      const targetFileId = getResolvedFileIdFromMeta(attachment);
      if (!targetFileId) {
        return;
      }
      seedAttachmentIndicatorHint(attachment, source);
      hydrateAttachmentIndicatorHint(attachment);

      const targetIds = Array.from(
        new Set(
          [
            targetFileId,
            attachment?.id,
            attachment?.fileId,
            attachment?.folderFileId,
            attachment?.file?.id,
            attachment?.file?.fileId,
            attachment?.file?.folderFileId,
          ]
            .filter((id) => id !== null && id !== undefined && id !== '')
            .map((id) => String(id)),
        ),
      );
      const docExistInGrid = currentAllDocuments.find((doc: any) => {
        const docIds = [
          doc?.id,
          doc?.fileId,
          doc?.folderFileId,
          doc?.file?.id,
          doc?.file?.fileId,
          doc?.file?.folderFileId,
        ]
          .filter((id) => id !== null && id !== undefined && id !== '')
          .map((id) => String(id));
        return docIds.some((id: string) => targetIds.includes(id));
      });

      if (docExistInGrid) {
        navigateToFile(getResolvedFileIdFromMeta(docExistInGrid) || targetFileId);
        dispatch(setCurrentSelectedFile(docExistInGrid));
        return;
      }

      (dispatch as any)(
        fetchFileDetails({
          fileId: targetFileId,
          folderFileId: attachment.folderFileId || attachment?.file?.folderFileId,
          firstTrialBookOnly: false,
        }),
      )
        .then((res: any) => {
          navigateToFile(targetFileId);
          dispatch(
            setCurrentSelectedFile({
              ...attachment,
              ...res,
            }),
          );
        })
        .catch((error: any) => {
          // Surface failures in dev tools to avoid silent errors; UI already keeps user in place
          console.error('Failed to fetch attachment details', error);
        });
    },
    [
      currentAllDocuments,
      dispatch,
      fetchFileDetails,
      getResolvedFileIdFromMeta,
      hydrateAttachmentIndicatorHint,
      navigateToFile,
      seedAttachmentIndicatorHint,
    ],
  );

  const handleAttachmentMenuItemClick = useCallback(
    (attachment: any) => handleAttachmentSelection(attachment, handleAttachmentsMenuClose, 'attachments'),
    [handleAttachmentSelection, handleAttachmentsMenuClose],
  );

  const handleAttachedToMenuItemClick = useCallback(
    (attachment: any) => {
      if (!getResolvedFileIdFromMeta(attachment)) {
        handleAttachedToMenuClose();
        return;
      }
      handleAttachedToMenuClose();
      handleAttachmentSelection(attachment, () => {}, 'isAttachedTo');
    },
    [getResolvedFileIdFromMeta, handleAttachedToMenuClose, handleAttachmentSelection],
  );

  useEffect(() => {
    if (!hasAttachments) {
      handleAttachmentsMenuClose();
    }
  }, [hasAttachments, handleAttachmentsMenuClose]);

  useEffect(() => {
    if (!hasAttachedTo) {
      handleAttachedToMenuClose();
    }
  }, [hasAttachedTo, handleAttachedToMenuClose]);

  useEffect(() => {
    const isFileOpened = window.location.href.includes('/files');
    if (!isAskAI && !isFileAskAI && !isFileOpened) {
      dispatch(setSummaryOpen(false));
    }
  }, []);

  useEffect(() => {
    if (prevFile && prevFile !== file) {
      setSearchDetails(null);
      setSearchFunctions(null);
    }
  }, [prevFile, file]);
  const updateRouteForFileAskAI = () => {
    let currentFileId = '';
    const currentPathname = history.location.pathname;

    // Extract file ID from pathname if we're on a file route
    if (currentPathname.includes('/files/')) {
      const pathSegments = currentPathname.split('/');
      currentFileId = pathSegments[pathSegments.length - 1];
    }

    // Update route if conditions are met
    if (!isAskAI && !isFileAskAI && currentFileId) {
      const existingParams = urlParams.toString();
      const paramPrefix = existingParams ? `${existingParams}&` : '';
      const newUrl = `${currentPathname}?${paramPrefix}fileAskAI=true`;
      history.push(newUrl);
    }
  };

  const removeFileAskAIFromRoute = () => {
    const currentPathname = history.location.pathname;
    const currentUrlParams = new URLSearchParams(window.location.search);

    // Remove fileAskAI parameter if it exists
    if (currentUrlParams.has('fileAskAI')) {
      currentUrlParams.delete('fileAskAI');
      const newSearch = currentUrlParams.toString();
      const newUrl = `${currentPathname}${newSearch ? `?${newSearch}` : ''}`;
      history.push(newUrl);
    }
  };
  // Determine compare files order (blue first, green second) from AI compare context if available
  const compareFilesOrder: string[] = useMemo(() => {
    if (!Array.isArray(searchMatchesInput) || searchMatchesInput.length === 0) return [];
    // Find the latest entry that includes compare.files
    for (let i = searchMatchesInput.length - 1; i >= 0; i -= 1) {
      const entry = searchMatchesInput[i];
      const files = entry?.compare?.files;
      if (Array.isArray(files) && files.length > 0) return files as string[];
    }
    return [];
  }, [searchMatchesInput]);

  // Resolve header background color based on file identity vs compare mapping
  const headerBackgroundColor = useMemo(() => {
    // Only colorize headers when compare context exists
    if (!fileToCompareMetaData?.id) return undefined;
    const currentId = fileId;
    if (compareFilesOrder.length >= 2) {
      if (currentId === compareFilesOrder[0]) return '#EBF3FE'; // blue for first compare file (anchor)
      // Check if current file is any of the comparison files (indices 1, 2, 3, ...)
      if (compareFilesOrder.slice(1).includes(currentId)) return '#EBFEF7'; // green for all other compare files
    }
    // Fallback to position-based color if compare mapping not available
    return isFileToCompare ? '#EBFEF7' : '#EBF3FE';
  }, [fileToCompareMetaData?.id, fileId, compareFilesOrder, isFileToCompare]);

  const sideBarHandler = (type?: string) => {
    let summaryOpened = false;

    if (openGlobally && !summaryOpen) {
      dispatch(setSidebarOpen(false));
      dispatch(setSummaryOpen(true));
      summaryOpened = true;
      if (type === 'isAI') {
        updateRouteForFileAskAI();
      }
    } else if (!openGlobally && !summaryOpen) {
      dispatch(setSummaryOpen(true));
      summaryOpened = true;
    } else if (summaryOpen) {
      // If summary is already open, close it
      dispatch(setSummaryOpen(false));
      // Remove fileAskAI parameter when summary is closed
      removeFileAskAIFromRoute();
      if (!openGlobally) {
        dispatch(setSidebarOpen(true));
      }
    }

    if (summaryOpened && type === 'isAI') {
      const urlWithAskAI = new URL(window.location.href);
      urlWithAskAI.searchParams.set('fileAskAI', 'true');
      history.push(urlWithAskAI.pathname + urlWithAskAI.search);
    }
  };
  const isGlobalPaging =
    globalPaging ||
    isGlobalPageLookUp ||
    (bundleSpecificFileDetails && Object.keys(bundleSpecificFileDetails).length > 0) ||
    (isPresentModePage &&
      currentFileMetaData &&
      Object.keys(currentFileMetaData).includes('folderId')) ||
    ((isTagsFolder || isSearchModeRefreshed) &&
      currentFileMetaData &&
      Object.keys(currentFileMetaData).includes('folderId')) ||
    currentFileMetaData?.globalPagePrefix;

  const fileStartPage =
    currentFileMetaData?.startPage && currentFileMetaData?.startPage.includes('.')
      ? currentFileMetaData?.startPage.split('.')
      : currentFileMetaData?.startPage;

  const isStartPageFirstDigitAfterDecimalZero =
    fileStartPage && fileStartPage.length > 1 && fileStartPage[1].startsWith('0');
  const currentFileId =
    currentFileMetaData?.folderFileId ||
    currentFileMetaData?.compositeKey ||
    currentFileMetaData.id ||
    '';
  const currentFileIndex = useMemo(() => {
    // If no current file metadata or required data, return -1
    if (!currentFileMetaData || !requiredDocumentsData?.length) {
      return -1;
    }

    // Step 1: First try to find by folderFileId (highest priority)
    if (currentFileMetaData.folderFileId) {
      const index = requiredDocumentsData.findIndex(
        (doc: any) => doc.folderFileId === currentFileMetaData.folderFileId,
      );
      if (index !== -1) return index;
    }

    // Step 2: Then try to find by compositeKey (second priority)
    if (currentFileMetaData.compositeKey) {
      const index = requiredDocumentsData.findIndex(
        (doc: any) => doc.compositeKey === currentFileMetaData.compositeKey,
      );
      if (index !== -1) return index;
    }

    // Step 3: Finally try to find by id (lowest priority)
    if (currentFileMetaData.id) {
      const index = requiredDocumentsData.findIndex(
        (doc: any) => doc.id === currentFileMetaData.id,
      );
      if (index !== -1) return index;
    }

    return -1;
  }, [requiredDocumentsData, currentFileMetaData]);
  // Determine if buttons should be disabled
  const disableActionBtns = useMemo(
    () => ({
      disablePrev: !currentFileId || currentFileIndex <= 0 || requiredDocumentsData.length <= 1,
      disableNext:
        !currentFileId ||
        currentFileIndex === requiredDocumentsData.length - 1 ||
        requiredDocumentsData.length <= 1,
    }),
    [currentFileId, currentFileIndex, requiredDocumentsData],
  );
  const prevNavDoc = currentFileIndex > 0 ? requiredDocumentsData[currentFileIndex - 1] : null;
  const nextNavDoc =
    currentFileIndex >= 0 && currentFileIndex < requiredDocumentsData.length - 1
      ? requiredDocumentsData[currentFileIndex + 1]
      : null;
  const buildNavTitle = (doc: any, labelKey: string) => {
    if (!doc) return null;
    const label = T.translate(labelKey);
    const docId = doc.id || doc.fileId || doc.folderFileId || doc.compositeKey || '';
    const docName =
      typeof doc.name === 'string'
        ? doc.name.trim()
        : typeof doc.fileName === 'string'
          ? doc.fileName.trim()
          : '';
    if (docName && docId) return `${label}: ${docName} /${docId}`;
    if (docName) return `${label}: ${docName}`;
    if (docId) return `${label}: ${docId}`;
    return label;
  };
  const prevNavTitle = buildNavTitle(prevNavDoc, 'generic.prevDoc');
  const nextNavTitle = buildNavTitle(nextNavDoc, 'generic.nextDoc');
  const shouldHideDocumentNav =
    currentPathname.includes('/case-intelligence') ||
    hasAskAIParam ||
    hasFileAskAIParam ||
    currentPathname.includes('/ai-tools') ||
    currentUrl.includes(`${caseId}/ai-tools`) ||
    (useAskAiSSEChat && summaryOpen && askAiSseHasResponseArea);
  const navigateToDocument = (newFolderFileId: string, newFileId: string, fileData: any) => {
    const baseUrl = history.location.pathname.replace(/\/files.*/, '');
    const presentParam = present ? `&present=${present}` : '';
    // Construct the new URL with the target document ID
    const newUrl = `${baseUrl}/files/${newFileId}${presentParam ? `?${presentParam.substring(1)}` : ''}`;
    // Update the URL and dispatch the current selected file
    history.push(newUrl);

    setTimeout(() => {
      dispatch(
        setCurrentSelectedFile({
          ...fileData,
        }),
      );
    }, 0);
    documents.forEach((doc: any) => {
      if (doc.folderFileId === newFolderFileId && doc.id === newFileId) {
        doc.selected = true;
      } else {
        doc.selected = false;
      }
    });
  };

  const prevClicked = () => {
    if (disableActionBtns.disablePrev) return;

    const prevFileData = requiredDocumentsData[currentFileIndex - 1];
    navigateToDocument(prevFileData?.folderFileId, prevFileData?.id, prevFileData);
  };

  const nextClicked = () => {
    if (disableActionBtns.disableNext) return;
    const nextFileData = requiredDocumentsData[currentFileIndex + 1];
    navigateToDocument(nextFileData?.folderFileId, nextFileData?.id, nextFileData);
  };

  // Determine if search button should be shown
  const shouldShowSearchButton = useMemo(() => {
    // Don't show if it's a file to compare
    if (isFileToCompare) return false;

    // Don't show if there's a file to compare metadata
    if (fileToCompareMetaData?.id) return false;

    // Don't show if compareFileData exists and has more than 1 file
    if (Array.isArray(compareFileData) && compareFileData.length > 1) return false;

    // Show if on AI tools page or showing search for file
    return currentUrl.includes(`${caseId}/ai-tools`) || showSearchForFile;
  }, [
    isFileToCompare,
    fileToCompareMetaData?.id,
    compareFileData,
    currentUrl,
    caseId,
    showSearchForFile,
  ]);
  const isCurrentFileMerged =
    typeof currentFileMetaData?.source === 'string' &&
    currentFileMetaData.source.toLowerCase() === 'merge';
  return (
    <div className="case-viewer-wrapper">
      <Container fluid className="h-100">
        <Row
          className="viewer-header-row justify-content-between align-items-center p-2 px-3"
          style={!fileToCompareMetaData?.id ? {} : { backgroundColor: headerBackgroundColor }}
        >
          <Col className="viewer-header-left pr-0">
            <Breadcrumb className="viewer-header-breadcrumb">
              <Breadcrumb.Item active={true}>
                <span className="viewer-header-title">
                  {isAskAI && compareFileName
                    ? compareFileName
                    : currentFileTitle}
                </span>
              </Breadcrumb.Item>
              {isBundleTabPage ? (
                <Breadcrumb.Item active={true} className="no-separator">
                  {currentFileMetaData.globalPagePrefix
                    ? `${currentFileMetaData.globalPagePrefix} / ${currentFileMetaData.tab}`
                    : ''}
                </Breadcrumb.Item>
              ) : (
                <GlobalPagingBreadcrumbItem
                  isGlobalPaging={isGlobalPaging}
                  currentFileMetaData={currentFileMetaData}
                  fileStartPage={fileStartPage}
                  isStartPageFirstDigitAfterDecimalZero={isStartPageFirstDigitAfterDecimalZero}
                />
              )}
              <ConfidentialityRing confidentialityRing={currentFileMetaData?.confidentialityRing} />
            </Breadcrumb>
          </Col>
          <Col
            xs="auto"
            className="viewer-header-right pl-0 d-flex justify-content-end align-items-center"
          >
            {(docSearchVal || docSearchLocation) &&
              searchFunctions &&
              fileViewerRef.current &&
              currentFileMetaData &&
              !isFileToCompare &&
              (fileId === currentFileMetaData.id ||
                (currentFileMetaData.file && fileId === currentFileMetaData.file.id)) && (
                <DocumentSearch
                  searchFunctions={searchFunctions}
                  docSearchVal={docSearchVal}
                  docSearchLocation={docSearchLocation}
                  isAiTools={currentUrl.includes(`${caseId}/ai-tools`)}
                  isSearchMode={isSearchMode}
                  sideSummaryOpen={summaryOpen}
                />
              )}
            {(docSearchValForCompare || docSearchLocationForCompare) &&
              searchFunctions &&
              fileViewerRef.current &&
              fileToCompareMetaData &&
              isFileToCompare &&
              (fileId === fileToCompareMetaData.id ||
                (fileToCompareMetaData.file && fileId === fileToCompareMetaData.file.id)) && (
                <DocumentSearch
                  searchFunctions={searchFunctions}
                  docSearchVal={docSearchValForCompare}
                  docSearchLocation={docSearchLocationForCompare}
                  isAiTools={currentUrl.includes(`${caseId}/ai-tools`)}
                  isSearchMode={isSearchMode}
                  sideSummaryOpen={summaryOpen}
                />
              )}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                marginTop: '2.2px',
              }}
            >
              <div className="actions-buttons">
                <Tooltip
                  title={T.translate('generic.focusView')}
                  enterDelay={0}
                  leaveDelay={0}
                  arrow
                >
                  <IconButton
                    id="focusViewer"
                    size="small"
                    onClick={handleOpenFocusView}
                    disabled={!canOpenFocusView}
                    sx={{
                      color: '#212529',
                      '&:hover': {
                        backgroundColor: 'rgba(69, 103, 229, 0)',
                      },
                      marginRight: '-0.5rem',
                    }}
                  >
                    <FilterCenterFocus fontSize="medium" />
                  </IconButton>
                </Tooltip>
                {hasAttachments && (
                  <>
                    <Tooltip
                      title={T.translate('generic.attachments')}
                      enterDelay={0}
                      leaveDelay={0}
                      arrow
                    >
                      <IconButton
                        id="attachments-menu-button"
                        size="small"
                        aria-haspopup="true"
                        aria-expanded={attachmentsMenuOpen ? 'true' : undefined}
                        aria-controls={attachmentsMenuOpen ? 'attachments-menu' : undefined}
                        onClick={handleAttachmentsIconClick}
                        sx={{
                          color: '#4567e5',
                          fontSize: '18px',
                          marginLeft: '0',
                          '&:hover': {
                            backgroundColor: 'rgba(69, 103, 229, 0)',
                          },
                        }}
                      >
                        <AttachEmail fontSize="medium" />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      id="attachments-menu"
                      anchorEl={attachmentsMenuAnchorEl}
                      open={attachmentsMenuOpen}
                      onClose={handleAttachmentsMenuClose}
                      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      MenuListProps={{
                        dense: true,
                      }}
                      disableScrollLock
                      PaperProps={{
                        sx: {
                          maxHeight: 350,
                          minWidth: 220,
                          py: 0,
                          border: '1px solid #cfcfcf',
                        },
                      }}
                    >
                      <div
                        style={{
                          padding: '15px 10px',
                          fontWeight: 600,
                          fontSize: '14px',
                          color: '#5c5c5c',
                        }}
                      >
                        {attachmentsHeadingLabel}
                      </div>
                      {attachments.map((attachment: any, index: number) => {
                        const attachmentId = attachment?.id || '-';
                        const rawName =
                          typeof attachment?.name === 'string' ? attachment.name.trim() : '';
                        const shouldTruncateName = rawName.length > ATTACHMENT_NAME_MAX_LENGTH;
                        const truncatedName = shouldTruncateName
                          ? rawName.slice(0, ATTACHMENT_NAME_MAX_LENGTH)
                          : rawName;

                        return (
                          <MenuItem
                            key={attachment?.id || `${attachment?.name || 'attachment'}-${index}`}
                            onClick={() => handleAttachmentMenuItemClick(attachment)}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              gap: '2px',
                              padding: '8px',
                            }}
                          >
                            <span style={{ fontWeight: 600, fontSize: '12px' }}>
                              {`${attachmentsIdLabel}: ${attachmentId}`}
                            </span>
                            <Tooltip
                              title={shouldTruncateName ? rawName : ''}
                              arrow
                              placement="top"
                            >
                              <span
                                style={{
                                  color: '#4a4a4a',
                                  display: 'flex',
                                  fontSize: '12px',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}
                              >
                                {`${attachmentsNameLabel}: ${truncatedName || '-'}`}
                                {shouldTruncateName && (
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      lineHeight: 1,
                                    }}
                                  >
                                    ...
                                  </span>
                                )}
                              </span>
                            </Tooltip>
                          </MenuItem>
                        );
                      })}
                    </Menu>
                  </>
                )}
                {hasAttachedTo && (
                  <>
                    <Tooltip title={attachedToHeadingLabel} enterDelay={0} leaveDelay={0} arrow>
                      <IconButton
                        id="attached-to-menu-button"
                        size="small"
                        aria-haspopup="true"
                        aria-expanded={attachedToMenuOpen ? 'true' : undefined}
                        aria-controls={attachedToMenuOpen ? 'attached-to-menu' : undefined}
                        onClick={handleAttachedToIconClick}
                        sx={{
                          padding: '0',
                          margin: '0',
                          marginLeft: '0',
                          '&:hover': {
                            backgroundColor: 'rgba(69, 103, 229, 0)',
                          },
                        }}
                      >
                        <FilePresent fontSize="medium" />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      id="attached-to-menu"
                      anchorEl={attachedToMenuAnchorEl}
                      open={attachedToMenuOpen}
                      onClose={handleAttachedToMenuClose}
                      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      MenuListProps={{
                        dense: true,
                      }}
                      disableScrollLock
                      PaperProps={{
                        sx: {
                          maxHeight: 350,
                          minWidth: 220,
                          py: 0,
                          border: '1px solid #cfcfcf',
                        },
                      }}
                    >
                      <div
                        style={{
                          padding: '15px 10px',
                          fontWeight: 600,
                          fontSize: '14px',
                          color: '#5c5c5c',
                        }}
                      >
                        {attachedToHeadingLabel}
                      </div>
                      {attachedToDocuments.map((attachment: any, index: number) => {
                        const attachmentId = attachment?.id || '-';
                        const rawName =
                          typeof attachment?.name === 'string' ? attachment.name.trim() : '';
                        const shouldTruncateName = rawName.length > ATTACHMENT_NAME_MAX_LENGTH;
                        const truncatedName = shouldTruncateName
                          ? rawName.slice(0, ATTACHMENT_NAME_MAX_LENGTH)
                          : rawName;

                        return (
                          <MenuItem
                            key={attachment?.id || `${attachment?.name || 'attachedTo'}-${index}`}
                            onClick={() => handleAttachedToMenuItemClick(attachment)}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              gap: '2px',
                              padding: '8px',
                            }}
                          >
                            <span style={{ fontWeight: 600, fontSize: '12px' }}>
                              {`${attachmentsIdLabel}: ${attachmentId}`}
                            </span>
                            <Tooltip
                              title={shouldTruncateName ? rawName : ''}
                              arrow
                              placement="top"
                            >
                              <span
                                style={{
                                  fontSize: '12px',
                                  color: '#4a4a4a',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}
                              >
                                {`${attachmentsNameLabel}: ${truncatedName || '-'}`}
                                {shouldTruncateName && (
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      lineHeight: 1,
                                    }}
                                  >
                                    ...
                                  </span>
                                )}
                              </span>
                            </Tooltip>
                          </MenuItem>
                        );
                      })}
                    </Menu>
                  </>
                )}

                {userAiTools &&
                !summaryOpen &&
                !fileToCompareMetaData?.id &&
                !isFileToCompare &&
                !currentUrl.includes(`${caseId}/ai-tools`) &&
                !isCurrentFileMerged &&
                !isAskAI ? (
                  <Tooltip title={T.translate('case.askAiTab')} enterDelay={0} leaveDelay={0} arrow>
                    <IconButton
                      onClick={() => sideBarHandler('isAI')}
                      size="small"
                      sx={{
                        color: '#4567e5',
                        opacity: 0.75,
                        marginRight: '0.3rem',
                        padding: '0 0 2px 0',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0)',
                          color: '#748CEC',
                          opacity: 1,
                        },
                      }}
                    >
                      <span
                        style={{
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '3px',
                        }}
                      >
                        <AutoAwesome />
                        {T.translate('case.ai')}
                      </span>
                    </IconButton>
                  </Tooltip>
                ) : (
                  <>
                    {canShowSummaryPane &&
                      !currentUrl.includes(`${caseId}/ai-tools`) &&
                      !userAiTools && (
                        <Tooltip
                          title={summaryOpen ? '' : T.translate('filters.searchThisDocument')}
                        >
                          <PhotoFilter
                            onClick={() => {
                              sideBarHandler();
                            }}
                            style={{ width: 20.4, height: 22, marginBottom: 2.5 }}
                          />
                        </Tooltip>
                      )}
                  </>
                )}
                {shouldShowSearchButton && (
                  <Tooltip
                    title={T.translate('case.openSearch')}
                    enterDelay={0}
                    leaveDelay={0}
                    arrow
                  >
                    <IconButton
                      onClick={onSearchClick}
                      sx={{
                        color: '#212529',
                        fontSize: 'inherit',
                        padding: '0 0 2px 0',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0)',
                        },
                      }}
                    >
                      <Search sx={{ fontSize: 20, color: '#212529' }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip
                  title={T.translate('case.openDocInNewTab')}
                  enterDelay={0}
                  leaveDelay={0}
                  arrow
                >
                  <IconButton
                    sx={{
                      color: '#212529',
                      fontSize: 'inherit',
                      padding: '0 0 2px 0',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0)',
                      },
                    }}
                  >
                    <FontAwesomeIcon
                      onClick={() => {
                        let goToPage = 1;
                        if (currentFileMetaData.type !== fileTypes.mp4) {
                          goToPage = fileViewerRef.current?.getCurrentPageNumber() || 1;
                        }
                        if (fileViewerRef.current?.pausePlayer) fileViewerRef.current.pausePlayer();
                        if (present) {
                          if (fileViewerRef.current?.stopPresent)
                            fileViewerRef.current.stopPresent();
                        }
                        const isGPExistCurrentFileMetaData = currentFileMetaData?.globalPagePrefix;
                        let urlWithParams = '';

                        if (fileType === fileTypes.mp4) {
                          if (present) {
                            urlWithParams = `&present=${present}`;
                          } else {
                            urlWithParams = '';
                          }
                        } else {
                          if (isGPExistCurrentFileMetaData && currentFileMetaData && present) {
                            // When metadata exists and present is true, include present param.
                            urlWithParams = `&tab=${currentFileMetaData.tab}&startPage=${currentFileMetaData.startPage}&pageCount=${currentFileMetaData.pageCount}&globalPagePrefix=${currentFileMetaData.globalPagePrefix}&present=${present}`;
                          } else if (
                            isGPExistCurrentFileMetaData &&
                            currentFileMetaData &&
                            !present
                          ) {
                            // When metadata exists but present is falsy, exclude present param.
                            urlWithParams = `&tab=${currentFileMetaData.tab}&startPage=${currentFileMetaData.startPage}&pageCount=${currentFileMetaData.pageCount}&globalPagePrefix=${currentFileMetaData.globalPagePrefix}`;
                          } else if (present && !isGPExistCurrentFileMetaData) {
                            urlWithParams = `&present=${present}`;
                          } else {
                            urlWithParams = '';
                          }
                        }
                        openNewBackgroundTab(
                          `/case/${caseId}/files/${fileId}?folderId=${currentFolder?.id || currentFileMetaData?.bundleLocations[0]?.folderId || currentFileMetaData?.folderId}${currentFileMetaData?.folderFileId ? `&folderFileId=${currentFileMetaData?.folderFileId}` : ''}${
                            goToPage > 1 ? `&goToPage=${goToPage}` : ''
                          }${urlWithParams}`,
                        );
                      }}
                      icon={faExternalLinkAlt}
                    />
                  </IconButton>
                </Tooltip>
                {(isFileToCompare || (askAiSearchType && !fileToCompareMetaData?.id)) && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      dispatch(setFileToCompare(null));
                      if (
                        !currentUrl.includes(`/ai-tools`) &&
                        askAiSearchType &&
                        !fileToCompareMetaData?.id
                      ) {
                        const currentPathname = history.location.pathname;
                        const baseMatch = currentPathname.match(/^(.*\/trialbundles\/[^/]+)/);

                        // Check if askAI=true parameter exists in current URL
                        const currentUrlParams = new URLSearchParams(history.location.search);
                        const hasAskAI = currentUrlParams.get('askAI') === 'true';
                        const askAIParam = hasAskAI ? '?askAI=true' : '';
                        const navState = hasAskAI
                          ? { skipAskAiHydration: true, skipAskAiReset: true }
                          : undefined;

                        if (baseMatch && baseMatch[1]) {
                          history.push(baseMatch[1] + askAIParam, navState);
                        } else {
                          history.push(
                            currentPathname.replace(/\/files.*/, '') + askAIParam,
                            navState,
                          );
                        }
                      }
                    }}
                    sx={{
                      padding: '2px',
                      minWidth: 'auto',
                      marginLeft: '8px',
                      marginRight: '0',
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                      },
                      '& svg': {
                        marginLeft: '0 !important',
                      },
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                )}
                {!isFileToCompare && !fileToCompareMetaData?.id && !summaryOpen && (
                  <Tooltip
                    title={T.translate('case.closePreviewSideBar')}
                    enterDelay={0}
                    leaveDelay={0}
                    arrow
                  >
                    <IconButton
                      sx={{
                        color: '#212529',
                        fontSize: 'inherit',
                        padding: '0 2px 0 0',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0)',
                        },
                      }}
                    >
                      <FontAwesomeIcon
                        onClick={() => {
                          const nextPath = history.location.pathname.replace(/\/files.*/, ``);
                          const currentState = history.location.state;
                          const baseState =
                            currentState && typeof currentState === 'object'
                              ? (currentState as Record<string, any>)
                              : {};
                          history.push(nextPath, {
                            ...baseState,
                            skipAskAiHydration: true,
                            skipAskAiReset: true,
                          });
                          // Persist last selected for highlight retention within same category
                          const pathParts = history.location.pathname.split('/');
                          // Expecting: ['', 'case', caseId, category, bundle, ...]
                          const categorySegment = pathParts[3] || null;
                          const bundleSegment = pathParts[4] || null;
                          if (currentFileMetaData && currentFileMetaData.id) {
                            dispatch(
                              setLastSelectedFile(
                                currentFileMetaData,
                                categorySegment,
                                bundleSegment,
                              ),
                            );
                          }
                          dispatch(setCurrentSelectedFile(null));
                          dispatch(setSummaryOpen(false));
                          dispatch(setFileToCompare(null));
                          if (!openGlobally) dispatch(setSidebarOpen(true));
                        }}
                        icon={faTimesCircle}
                      />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            </div>
          </Col>
        </Row>
        <Row className="secondRow">
          <Col md={12} className="h-100 pl-0">
            <Card className="viewer-document-card">
              {currentFileMetaData && Object.keys(currentFileMetaData).length > 0 && (
                <FileViewer
                  ref={fileViewerRef}
                  bundleSpecificFileDetails={bundleSpecificFileDetails}
                  isDocFinder={isDocFinder}
                  isFileToCompare={isFileToCompare}
                  searchHandler={searchHandler}
                  searchFunctionsHandler={searchFunctionsHandler}
                  currentFileMetaData={currentFileMetaData}
                  sideSummaryOpen={summaryOpen}
                  setSideSummaryOpen={(isOpen: boolean) => dispatch(setSummaryOpen(isOpen))}
                />
              )}
              {!shouldHideDocumentNav && (
                <>
                  <Tooltip
                    title={prevNavTitle || ''}
                    placement="top"
                    arrow
                    disableHoverListener={!prevNavTitle}
                  >
                    <span className="viewer-document-nav viewer-document-nav--prev">
                      <IconButton
                        id="updateFilePrevButton"
                        className="viewer-document-nav-button"
                        onClick={prevClicked}
                        disabled={disableActionBtns.disablePrev}
                        aria-label={T.translate('generic.prevDoc')}
                      >
                        <FontAwesomeIcon icon={faChevronLeft} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={nextNavTitle || ''}
                    placement="top"
                    arrow
                    disableHoverListener={!nextNavTitle}
                  >
                    <span className="viewer-document-nav viewer-document-nav--next">
                      <IconButton
                        id="updateFileNextButton"
                        className="viewer-document-nav-button"
                        onClick={nextClicked}
                        disabled={disableActionBtns.disableNext}
                        aria-label={T.translate('generic.nextDoc')}
                      >
                        <FontAwesomeIcon icon={faChevronRight} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};
